<template>
  <div id="order-pos-payment">
    <h1 class="font-bold">Pay Orders</h1>
    <hr class="mb-4" />
    <div id="infoauto-finder">
      <InfoautoCustomerFinder
        @selectedCustomer="selectedInfoautoCustomer"
        v-if="showInfoautoSearch"
        @close="showInfoautoSearch = false"
      ></InfoautoCustomerFinder>

      <div v-if="customer" class="pb-1 font-bold">
        {{ customer.nombreComp }}
      </div>
      <div v-if="!showInfoautoSearch">
        <button
          @click="showInfoautoSearch = true"
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          {{ customer ? "Change Customer" : "Select Customer" }}
        </button>
      </div>
    </div>
    <div v-if="customer" class="grid grid-cols-2 gap-4 mt-4 pt-2">
      <div id="orders" class="flex flex-col">
        <h2>Orders without payment</h2>
        <hr class="my-2" />
        <div class="flex-1 min-h-0">
          <BaseMiniSpinner v-if="loadingOrders" class="my-2"></BaseMiniSpinner>
          <div v-else class="orders-list">
            <ul>
              <li v-for="order in orders" :key="order.id" class="border-b py-2">
                <input
                  type="checkbox"
                  :name="order.orderNumber"
                  :id="order.orderNumber"
                  v-model="order.selected"
                  :disabled="order.notPaid == 0 || confirmed"
                />
                <label :for="order.orderNumber" class="py-2">
                  {{ order.orderNumber }} -
                  {{ order.total | currencyFormatted(true) }}
                  -
                  <span class="text-red-500">
                    {{ order.notPaid | currencyFormatted(true) }}
                  </span>
                  <span
                    class="ml-1 text-xs px-1 rounded"
                    :class="{
                      'bg-yellow-100 text-yellow-800':
                        order.status === 'Awaiting Payment',
                      'bg-blue-100 text-blue-800':
                        order.status === 'Awaiting Shipment',
                      'bg-orange-100 text-orange-800':
                        order.status === 'Processing',
                      'bg-purple-100 text-purple-800':
                        order.status === 'On Hold',
                      'bg-gray-100 text-gray-700': !order.status
                    }"
                  >
                    {{ order.status }}
                  </span>
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div>
        <h2>Information</h2>
        <hr class="my-2" />
        <div class="font-bold">Total amount to pay: ${{ totalSelected }}</div>
        <div>
          <div class="my-2 bg-gray-800 text-white p-2">
            Email: {{ customer.email1 }}
          </div>

          <div>
            <h3 class="text-xl font-bold">
              Order Total
              <b class="text-red-500">{{
                totalSelected | currencyFormatted(true)
              }}</b>
            </h3>
            <div class="flex items-center gap-2 my-2">
              <input
                type="checkbox"
                id="applyFee"
                v-model="applyFee"
                :disabled="confirmed"
              />
              <label for="applyFee" class="mb-0">Apply payment fee</label>
            </div>
            <button
              class="btn-dark-button"
              @click="confirmDataForPayment"
              v-if="!confirmed && totalSelected > 0"
            >
              Confirm
            </button>
          </div>

          <div v-if="confirmed">
            <hr class="my-3" />

            <div
              v-if="feeAmount > 0 || existingFeeAmount > 0"
              class="bg-gray-100 rounded p-3 mb-3 text-sm"
            >
              <div class="flex justify-between">
                <span>Subtotal</span>
                <span>${{ totalSelected }}</span>
              </div>
              <div
                v-if="existingFeeAmount > 0"
                class="flex justify-between text-gray-500"
              >
                <span>Fee already in orders</span>
                <span>${{ existingFeeAmount.toFixed(2) }}</span>
              </div>
              <div
                v-if="feeAmount > 0"
                class="flex justify-between text-orange-600"
              >
                <span>Payment fee applied</span>
                <span>+${{ feeAmount.toFixed(2) }}</span>
              </div>
              <div class="flex justify-between font-bold border-t mt-1 pt-1">
                <span>Total charged</span>
                <span>${{ totalWithFee.toFixed(2) }}</span>
              </div>
            </div>

            <div v-if="loadingStripe" class="flex items-center gap-2 py-4">
              <BaseMiniSpinner />
              <span>Initializing payment...</span>
            </div>

            <div v-else-if="paymentSuccess" class="text-center py-8">
              <div class="text-5xl mb-3">✓</div>
              <h3 class="text-xl font-bold text-green-600">
                Payment Successful!
              </h3>
              <button
                v-if="posTransactionId"
                class="mt-3 text-blue-600 underline text-sm"
                type="button"
                @click="$emit('openReceipt', posTransactionId)"
              >
                View POS Receipt #{{ posTransactionId }}
              </button>
              <table class="default-table mt-4">
                <tbody>
                  <tr>
                    <td colspan="2">Orders paid</td>
                  </tr>
                  <tr v-for="order in ordersPaid" :key="order.orderNumber">
                    <td>
                      {{ order.orderNumber }} (
                      <router-link
                        :to="{
                          name: 'OrdersPage',
                          query: { orderOpen: order.orderNumber }
                        }"
                        target="_blank"
                        class="text-blue-500"
                      >
                        Open Order
                      </router-link>
                      )
                    </td>
                    <td>${{ order.notPaid }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <StripeCardSection
              v-else-if="stripe && clientSecret"
              :stripe="stripe"
              :clientSecret="clientSecret"
              :amount="totalWithFee || totalSelected"
              :customerEmail="customer.email1"
              @confirmed="onPaymentConfirmed"
            >
              <template v-slot:cancel>
                <button
                  type="button"
                  class="px-6 py-3 border border-gray-300 rounded-md font-semibold hover:bg-gray-100"
                  @click="cancelPayment"
                >
                  Cancel
                </button>
              </template>
            </StripeCardSection>

            <div
              v-if="stripeError"
              class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4"
            >
              {{ stripeError }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import InfoautoCustomerFinder from "@/components/customer/InfoautoCustomerFinder.vue";
import { getOrders } from "@/api/store/OrdersService";
import StripeCardSection from "@/components/payments/StripeCardSection.vue";

export default {
  name: "OrderPosPayment",
  components: { StripeCardSection, InfoautoCustomerFinder },
  data() {
    return {
      showInfoautoSearch: false,
      customer: null,
      loadingOrders: false,
      orders: [],
      ordersPaid: [],
      confirmed: false,
      stripe: null,
      clientSecret: "",
      paymentIntentId: "",
      loadingStripe: false,
      stripeError: null,
      paymentSuccess: false,
      applyFee: false,
      feeAmount: 0,
      existingFeeAmount: 0,
      totalWithFee: 0,
      posTransactionId: null
    };
  },
  computed: {
    totalSelected() {
      return this.orders
        .reduce((acc, order) => {
          return order.selected ? acc + Number(order.notPaid) : acc;
        }, 0)
        .toFixed(2);
    }
  },
  methods: {
    cancelPayment() {
      this.confirmed = false;
      this.stripe = null;
      this.clientSecret = "";
      this.paymentIntentId = "";
      this.stripeError = null;
      this.feeAmount = 0;
      this.existingFeeAmount = 0;
      this.totalWithFee = 0;
    },
    selectedInfoautoCustomer(customer) {
      this.showInfoautoSearch = false;
      this.customer = customer;
      this.orders = [];
      this.ordersPaid = [];
      this.confirmed = false;
      this.stripe = null;
      this.clientSecret = "";
      this.stripeError = null;
      this.paymentSuccess = false;
      this.applyFee = false;
      this.feeAmount = 0;
      this.existingFeeAmount = 0;
      this.totalWithFee = 0;
      this.posTransactionId = null;
      this.loadOrders();
    },
    loadOrders() {
      this.loadingOrders = true;
      getOrders({
        customerInfoautoId: this.customer.personaId,
        otherFilters: { paidStatus: 0, hideCancelled: true, hideDraft: true }
      })
        .then(res => {
          this.orders = res.orders;
        })
        .catch(e => {
          console.error(e);
        })
        .finally(() => {
          this.loadingOrders = false;
        });
    },
    async confirmDataForPayment() {
      if (this.totalSelected == 0) {
        this.$toast.open({
          message: "Select at least one order",
          type: "error",
          position: "top-right"
        });
        return;
      }
      this.confirmed = true;
      this.loadingStripe = true;
      this.stripeError = null;

      const firstSelected = this.orders.find(o => o.selected);

      try {
        if (!this.stripe) {
          this.stripe = await loadStripe(
            process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
          );
        }
        const selectedOrders = this.orders
          .filter(o => o.selected)
          .map(o => ({
            orderNumber: o.orderNumber,
            amount: parseFloat(o.notPaid)
          }));

        const response = await axios.post(
          process.env.VUE_APP_WEB_API +
            "/stripex/payment_intent_existing_order",
          {
            order_number: firstSelected.orderNumber,
            orders: selectedOrders,
            amount: parseFloat(this.totalSelected),
            saveCard: false,
            isAdmin: true,
            applyFee: this.applyFee
          },
          {
            headers: {
              Authorization: this.$store.getters["auth/currentToken"]
            }
          }
        );
        this.clientSecret = response.data.secret;
        this.paymentIntentId = response.data.paymentIntentId;
        this.feeAmount = response.data.feeAmount || 0;
        this.existingFeeAmount = response.data.existingFeeAmount || 0;
        this.totalWithFee =
          response.data.totalWithFee || parseFloat(this.totalSelected);
      } catch (e) {
        this.stripeError =
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          "Failed to initialize payment";
        this.confirmed = false;
      } finally {
        this.loadingStripe = false;
      }
    },
    async onPaymentConfirmed(paymentIntentId) {
      try {
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
          const res = await axios.get(
            process.env.VUE_APP_WEB_API +
              `/stripex/verify_payment/${paymentIntentId}`
          );
          if (res.data.status === "succeeded") {
            this.ordersPaid = this.orders.filter(o => o.selected);
            this.paymentSuccess = true;
            this.posTransactionId = res.data.pos_transaction_id || null;
            this.$toast.open({
              message: "Payment processed successfully",
              type: "success",
              position: "top-right"
            });
            this.$emit(
              "newPaymentProcessed",
              this.posTransactionId || paymentIntentId
            );
            this.loadOrders();
            return;
          }
          if (res.data.status === "failed") {
            this.stripeError =
              res.data.message || "Payment verification failed.";
            return;
          }
          await new Promise(r => setTimeout(r, 1000));
        }
        this.stripeError = "Payment verification timed out.";
      } catch (e) {
        this.stripeError =
          e.response?.data?.message || "Failed to verify payment";
      }
    }
  }
};
</script>

<style lang="postcss">
#infoauto-finder {
  #myModal .modal-content {
    width: 90%;
    max-width: 800px;
  }
}

#order-pos-payment {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .grid {
    flex: 1;
    min-height: 0;
  }

  #orders {
    max-height: 70vh;

    .orders-list {
      max-height: 60vh;
      overflow-y: auto;
      overflow-x: hidden;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 8px;

      ul {
        margin: 0;
        padding: 0;
      }

      li {
        display: flex;
        align-items: center;
        gap: 8px;

        input[type="checkbox"] {
          flex-shrink: 0;
        }

        label {
          flex: 1;
          cursor: pointer;
          margin: 0;
        }
      }
    }
  }
}
</style>
